import { createContext, ReactNode, useEffect, useRef, useState } from "react";
import { ReportDetailEnum } from "../../../domain/enum/ReportDetailEnum";
import EcommerceManagementServices from "../../../services/EcommerceManagementServices";
import { getInstitutionId } from "../../../util/UserUtil";
import { ProgressReportViewModel } from "../../../domain/viewmodels/report/ProgressReportViewModel";
import { IndicatorsReportViewModel } from "../../../domain/viewmodels/report/IndicatorsReportViewModel";
import { FinancialReportViewModel } from "../../../domain/viewmodels/report/FinancialReportViewModel";
import { CourseOptionViewModel } from "../../../domain/viewmodels/report/CourseOptionViewModel";
import { ReportFilterPayload } from "../../../domain/payloads/progressReport/ReportFilterPayload";
import { FinancialReportFilterPayload } from "../../../domain/payloads/financialReport/FinancialReportFilterPayload";

const ecommerceService = new EcommerceManagementServices();

export interface ReportFilters {
    courseIds: string[];
    studentName: string;
    moduleStartDateFrom: Date | null;
    moduleStartDateTo: Date | null;
    moduleEndDateFrom: Date | null;
    moduleEndDateTo: Date | null;
}

interface PaginationState {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
}

export interface FinancialReportFilters {
    courseIds: string[];
    studentName: string;
    purchaseDateFrom: Date | null;
    purchaseDateTo: Date | null;
}

const defaultFinancialFilters: FinancialReportFilters = {
    courseIds: [],
    studentName: "",
    purchaseDateFrom: null,
    purchaseDateTo: null,
};

const defaultFilters: ReportFilters = {
    courseIds: [],
    studentName: "",
    moduleStartDateFrom: null,
    moduleStartDateTo: null,
    moduleEndDateFrom: null,
    moduleEndDateTo: null,
};

const defaultPagination: PaginationState = {
    currentPage: 1,
    pageSize: 30,
    totalRecords: 0,
    totalPages: 0,
};

interface ReportDetailsContextData {
    activeTab: ReportDetailEnum;
    setActiveTab: (tab: ReportDetailEnum) => void;

    syncStatusMessage: string;
    isLoadingSyncStatus: boolean;

    showFilterModal: boolean;
    setShowFilterModal: React.Dispatch<React.SetStateAction<boolean>>;
    showExportModal: boolean;
    setShowExportModal: React.Dispatch<React.SetStateAction<boolean>>;

    filters: ReportFilters;
    applyFilters: (newFilters: ReportFilters) => void;
    clearFilters: () => void;

    reportData: ProgressReportViewModel[];
    pagination: PaginationState;
    isLoading: boolean;
    error: string | null;

    goToPage: (page: number) => void;

    sortBy: string;
    sortDirection: 'asc' | 'desc';
    handleSort: (column: string) => void;

    courseOptions: CourseOptionViewModel[];
    loadCourseOptions: () => void;

    fetchReport: () => void;

    indicatorsFilters: ReportFilters;
    applyIndicatorsFilters: (newFilters: ReportFilters) => void;
    clearIndicatorsFilters: () => void;

    indicatorsData: IndicatorsReportViewModel[];
    indicatorsPagination: PaginationState;
    indicatorsLoading: boolean;
    indicatorsError: string | null;

    indicatorsGoToPage: (page: number) => void;

    indicatorsSortBy: string;
    indicatorsSortDirection: 'asc' | 'desc';
    handleIndicatorsSort: (column: string) => void;

    fetchIndicatorsReport: () => void;

    financialFilters: FinancialReportFilters;
    applyFinancialFilters: (newFilters: FinancialReportFilters) => void;
    clearFinancialFilters: () => void;

    financialData: FinancialReportViewModel[];
    financialPagination: PaginationState;
    financialLoading: boolean;
    financialError: string | null;

    financialGoToPage: (page: number) => void;

    financialSortBy: string;
    financialSortDirection: 'asc' | 'desc';
    handleFinancialSort: (column: string) => void;

    fetchFinancialReport: () => void;
}

function getLocalTimeFallback(): string {
    return `Dados atualizados às ${new Date().toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
    })}`;
}

export const ReportDetailsContext = createContext({} as ReportDetailsContextData);

export const ReportDetailsContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activeTab, setActiveTabRaw] = useState<ReportDetailEnum>(ReportDetailEnum.consumption);
    const [syncStatusMessage, setSyncStatusMessage] = useState<string>(getLocalTimeFallback());
    const [isLoadingSyncStatus, setIsLoadingSyncStatus] = useState<boolean>(true);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
    const [reportData, setReportData] = useState<ProgressReportViewModel[]>([]);
    const [pagination, setPagination] = useState<PaginationState>(defaultPagination);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<string>("");
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [courseOptions, setCourseOptions] = useState<CourseOptionViewModel[]>([]);

    const filtersRef = useRef(filters);
    const sortByRef = useRef(sortBy);
    const sortDirectionRef = useRef(sortDirection);
    const currentPageRef = useRef(1);

    filtersRef.current = filters;
    sortByRef.current = sortBy;
    sortDirectionRef.current = sortDirection;

    const [indicatorsFilters, setIndicatorsFilters] = useState<ReportFilters>(defaultFilters);
    const [indicatorsData, setIndicatorsData] = useState<IndicatorsReportViewModel[]>([]);
    const [indicatorsPagination, setIndicatorsPagination] = useState<PaginationState>(defaultPagination);
    const [indicatorsLoading, setIndicatorsLoading] = useState(false);
    const [indicatorsError, setIndicatorsError] = useState<string | null>(null);
    const [indicatorsSortBy, setIndicatorsSortBy] = useState<string>("");
    const [indicatorsSortDirection, setIndicatorsSortDirection] = useState<'asc' | 'desc'>('asc');

    const indicatorsFiltersRef = useRef(indicatorsFilters);
    const indicatorsSortByRef = useRef(indicatorsSortBy);
    const indicatorsSortDirectionRef = useRef(indicatorsSortDirection);
    const indicatorsCurrentPageRef = useRef(1);

    indicatorsFiltersRef.current = indicatorsFilters;
    indicatorsSortByRef.current = indicatorsSortBy;
    indicatorsSortDirectionRef.current = indicatorsSortDirection;

    const [financialFilters, setFinancialFilters] = useState<FinancialReportFilters>(defaultFinancialFilters);
    const [financialData, setFinancialData] = useState<FinancialReportViewModel[]>([]);
    const [financialPagination, setFinancialPagination] = useState<PaginationState>(defaultPagination);
    const [financialLoading, setFinancialLoading] = useState(false);
    const [financialError, setFinancialError] = useState<string | null>(null);
    const [financialSortBy, setFinancialSortBy] = useState<string>("");
    const [financialSortDirection, setFinancialSortDirection] = useState<'asc' | 'desc'>('asc');

    const financialFiltersRef = useRef(financialFilters);
    const financialSortByRef = useRef(financialSortBy);
    const financialSortDirectionRef = useRef(financialSortDirection);
    const financialCurrentPageRef = useRef(1);

    financialFiltersRef.current = financialFilters;
    financialSortByRef.current = financialSortBy;
    financialSortDirectionRef.current = financialSortDirection;

    const setActiveTab = (tab: ReportDetailEnum) => {
        if (tab === activeTab) return;

        if (tab === ReportDetailEnum.consumption) setIsLoading(true);
        if (tab === ReportDetailEnum.consumptionDetailed) setIndicatorsLoading(true);
        if (tab === ReportDetailEnum.financial) setFinancialLoading(true);

        setActiveTabRaw(tab);
    };

    useEffect(() => {
        fetchSyncStatus();
    }, []);

    useEffect(() => {
        // Reset all tabs' filters, sort, and pagination on every tab switch
        setFilters(defaultFilters);
        filtersRef.current = defaultFilters;
        setSortBy("");
        setSortDirection("asc");
        sortByRef.current = "";
        sortDirectionRef.current = "asc";
        currentPageRef.current = 1;
        setPagination(defaultPagination);

        setIndicatorsFilters(defaultFilters);
        indicatorsFiltersRef.current = defaultFilters;
        setIndicatorsSortBy("");
        setIndicatorsSortDirection("asc");
        indicatorsSortByRef.current = "";
        indicatorsSortDirectionRef.current = "asc";
        indicatorsCurrentPageRef.current = 1;
        setIndicatorsPagination(defaultPagination);

        setFinancialFilters(defaultFinancialFilters);
        financialFiltersRef.current = defaultFinancialFilters;
        setFinancialSortBy("");
        setFinancialSortDirection("asc");
        financialSortByRef.current = "";
        financialSortDirectionRef.current = "asc";
        financialCurrentPageRef.current = 1;
        setFinancialPagination(defaultPagination);

        if (activeTab === ReportDetailEnum.consumption) {
            doFetch(defaultFilters, 1, "", "asc");
        }
        if (activeTab === ReportDetailEnum.consumptionDetailed) {
            doFetchIndicators(defaultFilters, 1, "", "asc");
        }
        if (activeTab === ReportDetailEnum.financial) {
            doFetchFinancial(defaultFinancialFilters, 1, "", "asc");
        }
    }, [activeTab]);

    const fetchSyncStatus = async () => {
        const institutionId = getInstitutionId();
        if (!institutionId) {
            setIsLoadingSyncStatus(false);
            return;
        }

        setIsLoadingSyncStatus(true);
        const response = await ecommerceService.getLastSyncStatus(institutionId);

        if (response && response.success && response.result) {
            setSyncStatusMessage(response.result);
        }

        setIsLoadingSyncStatus(false);
    };

    const doFetch = async (
        activeFilters: ReportFilters,
        page: number,
        activeSortBy: string,
        activeSortDirection: 'asc' | 'desc'
    ) => {
        const institutionId = getInstitutionId();
        if (!institutionId) return;

        setIsLoading(true);
        setError(null);

        const toIso = (d: Date | null) => d ? d.toISOString().split('T')[0] : undefined;

        const payload: ReportFilterPayload = {
            courseIds: activeFilters.courseIds.length > 0 ? activeFilters.courseIds : undefined,
            studentName: activeFilters.studentName || undefined,
            moduleStartDateFrom: toIso(activeFilters.moduleStartDateFrom),
            moduleStartDateTo: toIso(activeFilters.moduleStartDateTo),
            moduleEndDateFrom: toIso(activeFilters.moduleEndDateFrom),
            moduleEndDateTo: toIso(activeFilters.moduleEndDateTo),
            sortBy: activeSortBy || undefined,
            sortDirection: activeSortBy ? activeSortDirection : undefined,
        };

        const response = await ecommerceService.getProgressReport(
            institutionId,
            payload,
            page,
            30
        );

        if (!response || !response.success) {
            setError("Não foi possível carregar os dados no momento. Tente novamente mais tarde.");
            setIsLoading(false);
            return;
        }

        setReportData(response.result);
        setPagination({
            currentPage: response.paging.currentPage,
            pageSize: response.paging.pageSize,
            totalRecords: response.paging.totalRecords,
            totalPages: response.paging.totalPages,
        });
        setIsLoading(false);
    };

    const fetchReport = () => {
        doFetch(filtersRef.current, currentPageRef.current, sortByRef.current, sortDirectionRef.current);
    };

    const applyFilters = (newFilters: ReportFilters) => {
        currentPageRef.current = 1;
        setFilters(newFilters);
        filtersRef.current = newFilters;
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setShowFilterModal(false);
        doFetch(newFilters, 1, sortByRef.current, sortDirectionRef.current);
    };

    const clearFilters = () => {
        currentPageRef.current = 1;
        setFilters(defaultFilters);
        filtersRef.current = defaultFilters;
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        doFetch(defaultFilters, 1, sortByRef.current, sortDirectionRef.current);
    };

    const goToPage = (page: number) => {
        currentPageRef.current = page;
        setPagination(prev => ({ ...prev, currentPage: page }));
        doFetch(filtersRef.current, page, sortByRef.current, sortDirectionRef.current);
    };

    const handleSort = (column: string) => {
        let newSortBy: string;
        let newDirection: 'asc' | 'desc';

        if (sortByRef.current !== column) {
            newSortBy = column;
            newDirection = 'asc';
        } else if (sortDirectionRef.current === 'asc') {
            newSortBy = column;
            newDirection = 'desc';
        } else {
            newSortBy = '';
            newDirection = 'asc';
        }

        setSortBy(newSortBy);
        setSortDirection(newDirection);
        sortByRef.current = newSortBy;
        sortDirectionRef.current = newDirection;
        currentPageRef.current = 1;
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        doFetch(filtersRef.current, 1, newSortBy, newDirection);
    };

    const loadCourseOptions = async () => {
        if (courseOptions.length > 0) return;
        const institutionId = getInstitutionId();
        if (!institutionId) return;

        const response = await ecommerceService.getCourseOptions(institutionId);
        if (response && response.success && response.result) {
            setCourseOptions(response.result);
        }
    };

    const doFetchIndicators = async (
        activeFilters: ReportFilters,
        page: number,
        activeSortBy: string,
        activeSortDirection: 'asc' | 'desc'
    ) => {
        const institutionId = getInstitutionId();
        if (!institutionId) return;

        setIndicatorsLoading(true);
        setIndicatorsError(null);

        const toIso = (d: Date | null) => d ? d.toISOString().split('T')[0] : undefined;

        const payload: ReportFilterPayload = {
            courseIds: activeFilters.courseIds.length > 0 ? activeFilters.courseIds : undefined,
            studentName: activeFilters.studentName || undefined,
            moduleStartDateFrom: toIso(activeFilters.moduleStartDateFrom),
            moduleStartDateTo: toIso(activeFilters.moduleStartDateTo),
            moduleEndDateFrom: toIso(activeFilters.moduleEndDateFrom),
            moduleEndDateTo: toIso(activeFilters.moduleEndDateTo),
            sortBy: activeSortBy || undefined,
            sortDirection: activeSortBy ? activeSortDirection : undefined,
        };

        const response = await ecommerceService.getIndicatorsReport(
            institutionId,
            payload,
            page,
            30
        );

        if (!response || !response.success) {
            setIndicatorsError("Não foi possível carregar os dados no momento. Tente novamente mais tarde.");
            setIndicatorsLoading(false);
            return;
        }

        setIndicatorsData(response.result);
        setIndicatorsPagination({
            currentPage: response.paging.currentPage,
            pageSize: response.paging.pageSize,
            totalRecords: response.paging.totalRecords,
            totalPages: response.paging.totalPages,
        });
        setIndicatorsLoading(false);
    };

    const fetchIndicatorsReport = () => {
        doFetchIndicators(indicatorsFiltersRef.current, indicatorsCurrentPageRef.current, indicatorsSortByRef.current, indicatorsSortDirectionRef.current);
    };

    const applyIndicatorsFilters = (newFilters: ReportFilters) => {
        indicatorsCurrentPageRef.current = 1;
        setIndicatorsFilters(newFilters);
        indicatorsFiltersRef.current = newFilters;
        setIndicatorsPagination(prev => ({ ...prev, currentPage: 1 }));
        setShowFilterModal(false);
        doFetchIndicators(newFilters, 1, indicatorsSortByRef.current, indicatorsSortDirectionRef.current);
    };

    const clearIndicatorsFilters = () => {
        indicatorsCurrentPageRef.current = 1;
        setIndicatorsFilters(defaultFilters);
        indicatorsFiltersRef.current = defaultFilters;
        setIndicatorsPagination(prev => ({ ...prev, currentPage: 1 }));
        doFetchIndicators(defaultFilters, 1, indicatorsSortByRef.current, indicatorsSortDirectionRef.current);
    };

    const indicatorsGoToPage = (page: number) => {
        indicatorsCurrentPageRef.current = page;
        setIndicatorsPagination(prev => ({ ...prev, currentPage: page }));
        doFetchIndicators(indicatorsFiltersRef.current, page, indicatorsSortByRef.current, indicatorsSortDirectionRef.current);
    };

    const doFetchFinancial = async (
        activeFilters: FinancialReportFilters,
        page: number,
        activeSortBy: string,
        activeSortDirection: 'asc' | 'desc'
    ) => {
        const institutionId = getInstitutionId();
        if (!institutionId) return;

        setFinancialLoading(true);
        setFinancialError(null);

        const toIso = (d: Date | null) => d ? d.toISOString().split('T')[0] : undefined;

        const payload: FinancialReportFilterPayload = {
            courseIds: activeFilters.courseIds.length > 0 ? activeFilters.courseIds : undefined,
            studentName: activeFilters.studentName || undefined,
            purchaseDateFrom: toIso(activeFilters.purchaseDateFrom),
            purchaseDateTo: toIso(activeFilters.purchaseDateTo),
            sortBy: activeSortBy || undefined,
            sortDirection: activeSortBy ? activeSortDirection : undefined,
        };

        const response = await ecommerceService.getFinancialReport(
            institutionId,
            payload,
            page,
            30
        );

        if (!response || !response.success) {
            setFinancialError("Não foi possível carregar os dados no momento. Tente novamente mais tarde.");
            setFinancialLoading(false);
            return;
        }

        setFinancialData(response.result);
        setFinancialPagination({
            currentPage: response.paging.currentPage,
            pageSize: response.paging.pageSize,
            totalRecords: response.paging.totalRecords,
            totalPages: response.paging.totalPages,
        });
        setFinancialLoading(false);
    };

    const fetchFinancialReport = () => {
        doFetchFinancial(financialFiltersRef.current, financialCurrentPageRef.current, financialSortByRef.current, financialSortDirectionRef.current);
    };

    const applyFinancialFilters = (newFilters: FinancialReportFilters) => {
        financialCurrentPageRef.current = 1;
        setFinancialFilters(newFilters);
        financialFiltersRef.current = newFilters;
        setFinancialPagination(prev => ({ ...prev, currentPage: 1 }));
        setShowFilterModal(false);
        doFetchFinancial(newFilters, 1, financialSortByRef.current, financialSortDirectionRef.current);
    };

    const clearFinancialFilters = () => {
        financialCurrentPageRef.current = 1;
        setFinancialFilters(defaultFinancialFilters);
        financialFiltersRef.current = defaultFinancialFilters;
        setFinancialPagination(prev => ({ ...prev, currentPage: 1 }));
        doFetchFinancial(defaultFinancialFilters, 1, financialSortByRef.current, financialSortDirectionRef.current);
    };

    const financialGoToPage = (page: number) => {
        financialCurrentPageRef.current = page;
        setFinancialPagination(prev => ({ ...prev, currentPage: page }));
        doFetchFinancial(financialFiltersRef.current, page, financialSortByRef.current, financialSortDirectionRef.current);
    };

    const handleFinancialSort = (column: string) => {
        let newSortBy: string;
        let newDirection: 'asc' | 'desc';

        if (financialSortByRef.current !== column) {
            newSortBy = column;
            newDirection = 'asc';
        } else if (financialSortDirectionRef.current === 'asc') {
            newSortBy = column;
            newDirection = 'desc';
        } else {
            newSortBy = '';
            newDirection = 'asc';
        }

        setFinancialSortBy(newSortBy);
        setFinancialSortDirection(newDirection);
        financialSortByRef.current = newSortBy;
        financialSortDirectionRef.current = newDirection;
        financialCurrentPageRef.current = 1;
        setFinancialPagination(prev => ({ ...prev, currentPage: 1 }));
        doFetchFinancial(financialFiltersRef.current, 1, newSortBy, newDirection);
    };

    const handleIndicatorsSort = (column: string) => {
        let newSortBy: string;
        let newDirection: 'asc' | 'desc';

        if (indicatorsSortByRef.current !== column) {
            newSortBy = column;
            newDirection = 'asc';
        } else if (indicatorsSortDirectionRef.current === 'asc') {
            newSortBy = column;
            newDirection = 'desc';
        } else {
            newSortBy = '';
            newDirection = 'asc';
        }

        setIndicatorsSortBy(newSortBy);
        setIndicatorsSortDirection(newDirection);
        indicatorsSortByRef.current = newSortBy;
        indicatorsSortDirectionRef.current = newDirection;
        indicatorsCurrentPageRef.current = 1;
        setIndicatorsPagination(prev => ({ ...prev, currentPage: 1 }));
        doFetchIndicators(indicatorsFiltersRef.current, 1, newSortBy, newDirection);
    };

    return (
        <ReportDetailsContext.Provider value={{
            activeTab, setActiveTab,
            syncStatusMessage, isLoadingSyncStatus,
            showFilterModal, setShowFilterModal,
            showExportModal, setShowExportModal,
            filters, applyFilters, clearFilters,
            reportData, pagination, isLoading, error,
            goToPage,
            sortBy, sortDirection, handleSort,
            courseOptions, loadCourseOptions,
            fetchReport,
            indicatorsFilters, applyIndicatorsFilters, clearIndicatorsFilters,
            indicatorsData, indicatorsPagination, indicatorsLoading, indicatorsError,
            indicatorsGoToPage,
            indicatorsSortBy, indicatorsSortDirection, handleIndicatorsSort,
            fetchIndicatorsReport,
            financialFilters, applyFinancialFilters, clearFinancialFilters,
            financialData, financialPagination, financialLoading, financialError,
            financialGoToPage,
            financialSortBy, financialSortDirection, handleFinancialSort,
            fetchFinancialReport,
        }}>
            {children}
        </ReportDetailsContext.Provider>
    );
};
